"use client";

import React from "react";
import { Clock, ShieldCheck, PhoneCall, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, Loader as Loader2, Wrench, Check } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
const trustItems = [{
  icon: Clock,
  title: "24/7 Rapid dispatch",
  description: "Emergency teams on call around the clock across Jonesboro."
}, {
  icon: ShieldCheck,
  title: "Licensed and insured",
  description: "State-certified master plumbers equipped with heavy-duty diagnostic cameras."
}, {
  icon: Wrench,
  title: "Clean excavation & repair",
  description: "Trenchless options and full sewer line replacements with clear estimates."
}];
function SewerFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-card rounded-xl border border-border">
        <div className="size-14 rounded-full bg-accent/20 flex items-center justify-center text-accent-foreground mb-4">
          <Check className="size-7 text-primary" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">Request submitted</h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6 leading-relaxed">
          {successMessage || "Your request has been received. We'll contact you shortly."}
        </p>
        <Button type="button" onClick={resetForm} variant="outline" className="rounded-full px-6 text-sm">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold text-foreground uppercase tracking-wider">
                Full Name *
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background border-input text-foreground h-11 px-3.5 focus-visible:ring-accent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold text-foreground uppercase tracking-wider">
                Phone Number *
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" className="bg-background border-input text-foreground h-11 px-3.5 focus-visible:ring-accent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <FormField control={control} name="property_address" rules={{
          required: "Property address is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Property Address *
                </FormLabel>
                <FormControl>
                  <Input placeholder="123 Main St" className="bg-background border-input text-foreground h-11 px-3.5 focus-visible:ring-accent" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div>
          <FormField control={control} name="city" rules={{
          required: "City is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  City *
                </FormLabel>
                <FormControl>
                  <Input placeholder="Jonesboro" className="bg-background border-input text-foreground h-11 px-3.5 focus-visible:ring-accent" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <FormField control={control} name="urgency" rules={{
      required: "Please select your service urgency"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold text-foreground uppercase tracking-wider">
              Urgency Level *
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="w-full bg-background border-input text-foreground h-11 px-3.5 focus-visible:ring-accent">
                  <SelectValue placeholder="Select timeframe" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover text-popover-foreground border-border">
                <SelectItem value="Immediate Emergency">Immediate Emergency</SelectItem>
                <SelectItem value="Within 24 Hours">Within 24 Hours</SelectItem>
                <SelectItem value="This Week">This Week</SelectItem>
                <SelectItem value="Just Exploring Options">Just Exploring Options</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="issue_description" rules={{
      required: "Please describe the sewer issue"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold text-foreground uppercase tracking-wider">
              Issue Description *
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the sewer line problem (e.g., sewage backup, slow multiple drains, foul odor in yard)..." className="bg-background border-input text-foreground min-h-[90px] px-3.5 py-2.5 focus-visible:ring-accent" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {/* Hidden Source Page Field */}
      <input type="hidden" name="source_page" value="Sewer Line Repair Page" />

      <FormField control={control} name="consent" rules={{
      required: "You must agree to continue"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-1">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5 border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-xs text-muted-foreground font-normal leading-relaxed cursor-pointer">
                I agree to be contacted about my sewer service request via call or text.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <Alert variant="destructive" className="py-2.5">
          <AlertTriangle className="size-4" />
          <AlertTitle className="text-xs font-semibold">Error</AlertTitle>
          <AlertDescription className="text-xs">{submitError}</AlertDescription>
        </Alert>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold shadow-md hover:shadow-lg transition-[color,box-shadow,background-color] text-base cursor-pointer mt-2">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Submitting request...
          </> : "Request Sewer Service"}
      </Button>
    </Form>;
}
export default function SewerRequest() {
  const shouldReduceMotion = useReducedMotion();
  const motionProps = shouldReduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 14
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.32,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="sewerlineservicerequest" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/60 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {/* Left Column: Heading, Context & Trust Badges */}
          <motion.div {...motionProps} className="lg:col-span-5 flex flex-col space-y-8">
            <div className="space-y-4">
              <Badge variant="outline" className="bg-accent/15 text-accent-foreground border-accent/40 rounded-full px-3 py-1 font-semibold text-xs uppercase tracking-wider">
                Immediate Plumber Dispatch
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight leading-tight">
                Get sewer line repair service
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Persistent blockages, gurgling drains, and yard leaks require immediate, professional attention. Connect directly with our Jonesboro sewer specialists.
              </p>
            </div>

            {/* Direct Call Box */}
            <div className="bg-primary text-primary-foreground p-6 rounded-2xl shadow-md space-y-3">
              <div className="flex items-center space-x-2 text-accent">
                <PhoneCall className="size-5 shrink-0" />
                <span className="text-xs font-bold uppercase tracking-wider">
                  Critical Emergency Line
                </span>
              </div>
              <p className="text-xs text-primary-foreground/80">
                Sewage backing into your home? Speak with an active field supervisor right now.
              </p>
              <div className="pt-1">
                <a href="tel:4045550199" className="inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 rounded-full bg-accent text-accent-foreground font-bold hover:bg-accent/90 transition-colors shadow-sm text-sm">
                  <PhoneCall className="size-4 mr-2" />
                  Call (404) 555-0199
                </a>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="space-y-4 pt-2">
              {trustItems.map((item, i) => {
              const IconComp = item.icon;
              return <div key={item.title} data-index={i} className="flex items-start space-x-3.5 p-3 rounded-xl bg-card border border-border/80 shadow-2xs">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                      <IconComp className="size-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground leading-tight">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                        {item.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Right Column: Service Request Form Card */}
          <motion.div {...motionProps} transition={shouldReduceMotion ? {} : {
          duration: 0.32,
          delay: 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7">
            <Card className="bg-card text-card-foreground border-border shadow-lg rounded-2xl overflow-hidden p-2 sm:p-4">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
                    Sewer Line Service Request
                  </CardTitle>
                  <Badge variant="secondary" className="rounded-full text-xs shrink-0">
                    Fast Response
                  </Badge>
                </div>
                <CardDescription className="text-muted-foreground text-sm">
                  Complete the details below to dispatch a technician to your Jonesboro property.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                <FormProvider formId="sewer_line_service_form" sectionName="sewer-line-service-request" defaultValues={{
                full_name: "",
                phone: "",
                property_address: "",
                city: "Jonesboro",
                urgency: "Within 24 Hours",
                issue_description: "",
                consent: false,
                source_page: "Sewer Line Repair Page"
              }} validationRules={{
                full_name: {
                  required: true
                },
                phone: {
                  required: true
                },
                property_address: {
                  required: true
                },
                city: {
                  required: true
                },
                urgency: {
                  required: true
                },
                issue_description: {
                  required: true
                },
                consent: {
                  required: true
                }
              }} submitText="Request Sewer Service" successMessage="Your request has been received. We'll contact you shortly.">
                  <SewerFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}